import { z } from 'zod';

/**
 * Lowercase names every object carries (records of amounts are plain objects); the pattern
 * already rules out the others (`__proto__`, `toString`, `valueOf`).
 */
const RESERVED_IDS: ReadonlySet<string> = new Set(['constructor', 'prototype']);

/**
 * Items are data (ITEM-DEFINITION, Docs/GDD/sistemas/ITEM-DEFINITION.md): their definitions live
 * in the game config, which this package cannot see. The wire only knows an id's format; the
 * server checks that it exists. Ids are a public contract (stored, sent, used as i18n keys
 * `items.<id>`): never rename one.
 */
export const ItemIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/)
  .max(32)
  // Amounts are plain records: an id must never name what every object already has.
  .refine((id) => !RESERVED_IDS.has(id), { message: 'reserved item id' });
export type ItemId = z.infer<typeof ItemIdSchema>;

/** Quantities are whole units; a single stack never exceeds this (keeps sums safe in SQL). */
export const MAX_QUANTITY = 1_000_000;

export const QuantitySchema = z.number().int().nonnegative().max(MAX_QUANTITY);

/** A positive amount moved by a command. */
export const PositiveQuantitySchema = z.number().int().positive().max(MAX_QUANTITY);

/**
 * Amounts of items by id: holdings, costs, yields, losses. Absent items count as zero, so the
 * shape never changes when items are added.
 */
export const ItemAmountsSchema = z.record(ItemIdSchema, QuantitySchema);
export type ItemAmounts = z.infer<typeof ItemAmountsSchema>;

/** The amount of `item` in `amounts` (zero when absent). */
export const amountOf = (amounts: Readonly<ItemAmounts>, item: ItemId): number =>
  Object.hasOwn(amounts, item) ? (amounts[item] ?? 0) : 0;

/** Every item with a positive amount, in the order the amounts list them. */
export const itemsIn = (amounts: Readonly<ItemAmounts>): [ItemId, number][] =>
  Object.entries(amounts).filter(([, quantity]) => quantity > 0);
